// @ts-nocheck
import clsx from "clsx";
import { getIn } from "formik";
import _ from "lodash";
import React, { useMemo } from 'react';
import { t } from "ttag";
import { Button, IconButton } from '@coolremote/shared';
import { Delete } from "../../icons";
import { useStoreState } from "../../models/RootStore";
import Tooltip from "../Tooltip/LightTooltip";
import styles from "./AutomationRule.style";
import { getCustomOptions } from "./conditionUtils";
import { CustomAutoComplete, CustomedTextField, CustomSelect } from "./CustomedComponents";
import useTypes from '@hooks/useTypes';
import { useIsModbusEnabled } from "../../screens/Modbus/modbusFeature";

const indoorActionOptions = [
  { value: 1, enum: "unitOperationStatuses", name: "ON/OFF" },
  { value: 3, data_unit_of_measurement: "°C", name: "Setpoint" },
  { value: 4, name: "Fan", enum: "unitFanModes" },
  { value: 2, enum: "unitOperationModes", name: "Mode" },
];

const actionWRCOptions = [
  { value: 8, enum: "trueFalseEnum", name: "WRC On/Off" },
  { value: 9, enum: "trueFalseEnum", name: "WRC Mode" },
  { value: 10, enum: "trueFalseEnum", name: "WRC Setpoint" },
];

const hRVactionOptions = [
  { value: 1, enum: "unitOperationStatuses", name: "ON/OFF" },
  { value: 4, name: "Fan", enum: "unitFanModes" },
  { value: 2, enum: "unitOperationModes", name: "Mode" },
];
const HpAutoModeTooltipContent = () => {
  const classes = styles();
  return (
    <div>
      <div>{t`Applying Auto Sequence:`}</div>
      <ol className={classes.tooltipSequenceList}>
        <li>{t`Check which units are ON`}</li>
        <li>{t`Turn all units OFF`}</li>
        <li>{t`Change mode to the new defined Mode`}</li>
        <li>{t`Turn previously active units back ON`}</li>
      </ol>
    </div>
  );
};

const onOffSensorEnums = [{ value: "0", name: t`OFF` }, { value: 1, name: t`ON` }];
const systemActionOptions = [
  { value: 1, enum: "unitOperationStatuses", name: "ON/OFF" },
  { value: 3, data_unit_of_measurement: "°C", name: "Setpoint" },
  { value: 4, name: "Fan", enum: "unitFanModes" },
  { value: 2, enum: "unitOperationModes", name: "Mode" },
  { value: 12, enum: "unitOperationModes", name: "HP Auto Mode Change", tooltip: <HpAutoModeTooltipContent /> }
];

const sensorActionOptions = [
  { value: 6, name: "Value" },
];

const bacNetUnitActionOptions = [];

const modbusUnitActionOptions = [];

const soruceOptions = [
  { value: "systems", name: "System" },
  { value: "outputSensors", name: "Output Device" },
  { value: "indoors", name: "Indoor Unit" },
  { value: "alert", name: "Alert" },
  { value: 'WRCLocks', name: 'WRC locks' },
];

const selectItemPlaceholders = {
  systems: t`Select System`,
  outputSensors: t`Select Sensor`,
  indoors: t`Select Indoor Unit`,
  outdoors: t`Select Outdoor Unit`,
  sensors: t`Select Sensor`,
  powerMeters: t`Select Power Meter`,
  "": t`Select`,
  bacNetUnits: t`Select BACnet Device`,
  modbusUnits: t`Select Modbus Device`,
  WRCLocks: t`Select Indoor Unit`
};

const selectItemLabels = {
  systems: t`System`,
  outputSensors: t`Sensor`,
  indoors: t`Indoor Unit`,
  outdoors: t`Outdoor Unit`,
  sensors: t`Sensor`,
  powerMeters: t`Power Meter`,
  '': t`Select`,
  bacNetUnits: t`BACnet Device`,
  modbusUnits: t`Modbus Device`,
  WRCLocks: t`Indoor Unit`
};

const alertAttachToBaseOptions = [
  { value: 'site', name: t`Site` },
  { value: 'indoors', name: t`Indoor Unit` },
  { value: 'outdoors', name: t`Outdoor Unit` },
];

const alertAttachToTrailingOptions = [
  { value: 'sensors', name: t`Sensor` },
  { value: 'powerMeters', name: t`Power Meter` },
  { value: 'systems', name: t`System` },
];

const actionsOptionsMapBySource = {
  systems: systemActionOptions,
  outputSensors: sensorActionOptions,
  indoors: indoorActionOptions, // default for indoors
  WRCLocks: actionWRCOptions,
  bacNetUnits: bacNetUnitActionOptions,
  modbusUnits: modbusUnitActionOptions
};

const getActionOptions = (source, item, bacNetObjectsByTemplateId, modbusObjectsByUnitId) => {
  // handle the "indoors" source with a special case for hrv indoors "subtype 2"
  if (source === 'indoors' && item?.subType === 2) {
    return hRVactionOptions;
  }

  if (source === 'bacNetUnits') {
    return getBacNetObjectOptions(item, bacNetObjectsByTemplateId);
  }

  if (source === 'modbusUnits') {
    return getModbusObjectOptions(item, modbusObjectsByUnitId);
  }

  // return the options based on the source type
  return actionsOptionsMapBySource[source] || [];
};

const getBacNetObjectOptions = (item, objectsByTemplateId) => {
  if (!item) {
    return bacNetUnitActionOptions;
  }

  return [
    ...(objectsByTemplateId[item.paramTemplate] || []),
    ...bacNetUnitActionOptions
  ];
};

const getModbusObjectOptions = (item, objectsByUnitId) => {
  if (!item) {
    return modbusUnitActionOptions;
  }

  return [
    ...(objectsByUnitId?.[item.unitId] || []),
    ...modbusUnitActionOptions
  ];
};

const Action = (props: any) => {
  const classes = styles();
  const {
    errors,
    touched,
    index,
    action,
    setFieldValue,
    serviceParamTypes,
    customEnumOptions,
    site,
    values,
    deleteAction,
    setValues,
    disabled = false,
    duplicateAction = null
  } = props;

  const {
    temperatureScale,
  } = useStoreState((s) => s.users.me);

  const {
    enableBacnet
  } = useStoreState((s) => s.config);
  const enableModbus = useIsModbusEnabled();

  const getUnitsBy = useStoreState((s) => s.units.getUnitsBy);

  const {
    trapActionTypes,
    bacnetMeasurementUnits,
    trapActionParameterOperators,
    externalSetpointOptions,
  } = useTypes();

  const actionEnumMap = {
    [trapActionTypes?.setUnitFanMode]: "unitFanModes",
    [trapActionTypes?.setFullModeCycle]: "unitOperationModes",
    [trapActionTypes?.setUnitPowerState]: "unitOperationStatuses",
    [trapActionTypes?.setUnitOperationMode]: "unitOperationModes"
  };

  const expandedSourceOptions = useMemo(() => {
    const options = [...soruceOptions];
    if (enableBacnet) {
      options.push({ value: 'bacNetUnits', name: 'BACnet Device' });
    }
    if (enableModbus) {
      options.push({ value: 'modbusUnits', name: 'Modbus Device' });
    }
    return options;
  }, [enableBacnet, enableModbus]);

  const alertAttachToOptions = useMemo(() => {
    const options = [...alertAttachToBaseOptions];
    if (enableBacnet) {
      options.push({ value: 'bacNetUnits', name: t`BACnet Device` });
    }
    if (enableModbus) {
      options.push({ value: 'modbusUnits', name: t`Modbus Device` });
    }
    options.push(...alertAttachToTrailingOptions);
    return options;
  }, [enableBacnet, enableModbus]);

  const actionOptions = useMemo(() =>
    getActionOptions(
      action.source,
      action.itemId,
      site.bacNetObjectsByTemplateId,
      site.modbusObjectsByUnitId
    ),
    [action.source, action.itemId, site.bacNetObjectsByTemplateId, site.modbusObjectsByUnitId]
  );

  const externalUnitOptions = useMemo(() => {
    if (action.source !== 'systems' || !action.itemId?.value) return [];
    const options = getUnitsBy("system", action.itemId.value, { type: "control" })
      .filter((u: any) => u.subType !== 1)
      .map((u: any) => ({ value: u.id, name: u.name, type: u.type, subType: u.subType }));
    const systemUnitIds = new Set(options.map((u: any) => u.value));
    const selected = action.externalSourceUnitId;
    if (selected?.value && !systemUnitIds.has(selected.value)) {
      options.push({ ...selected, name: `${selected.name} (${t`not in system`})` });
    }
    return options;
  }, [action.source, action.itemId?.value, action.externalSourceUnitId, getUnitsBy]);

  const filteredOperators = useMemo(() => {
    if (!trapActionParameterOperators) return [];

    const isExternalApplicable =
      (action.source === 'indoors' || action.source === 'systems') &&
      +action.action === trapActionTypes.setUnitSetpoint;
    if (isExternalApplicable) return trapActionParameterOperators;

    return trapActionParameterOperators.filter((op: any) => op.value !== 'external');
  }, [action.source, action.action, trapActionParameterOperators, trapActionTypes]);

  const renderValueField = () => {
    if (action.action === trapActionTypes.createEvent) {
      return null;
    }

    let type;
    let options;
    let enumOptions;
    let label = t`Value`;

    if (action.source === 'outputSensors') {
      type = 'number';
      enumOptions = action.itemId?.type === 129 && onOffSensorEnums;
    } else if (action.source === 'bacNetUnits') {
      const bacnetUnitObjects = site.bacNetObjectsByTemplateId[action.itemId?.paramTemplate] ?? [];
      const object = bacnetUnitObjects.find(object => object.code === action.action);
      enumOptions = object?.enum && getCustomOptions(action.source, object.enum);

      if (object?.data_unit_of_measurement) {
        label = label + ` (${bacnetMeasurementUnits[object.data_unit_of_measurement] ?? t`unknown`})`;
      }
    } else if (action.source === 'modbusUnits') {
      const modbusUnitObjects = site.modbusObjectsByUnitId?.[action.itemId?.unitId] ?? [];
      const object = modbusUnitObjects.find(object => object.code === action.action);
      enumOptions = object?.enum && getCustomOptions(action.source, object.enum);

      if (object?.data_unit_of_measurement) {
        label = label + ` (${object.data_unit_of_measurement})`;
      }
    } else {
      enumOptions = actionEnumMap[action.action] ? getCustomOptions(
        action.source,
        actionEnumMap[action.action],
        action.itemId?.subType,
        serviceParamTypes,
        customEnumOptions
      ) : null;

      if (+action.action === trapActionTypes.setUnitSetpoint) {
        label = label + `${temperatureScale === 2 ? ' °F' : ' °C'}`;
      }
    }

    const operatorSelect = (
      <CustomSelect
        disabled={disabled || !!enumOptions || !action.itemId || !action.action}
        error={getIn(errors, `actions[${index}].operator`) && getIn(touched, `actions[${index}].operator`)}
        placeholder={t`Action Type`}
        name='operator'
        value={action.operator || '='}
        onChange={(event) => {
          setFieldValue(`actions[${index}].operator`, event.target.value);
          setFieldValue(`actions[${index}].value`, '');
          if (event.target.value !== 'external') {
            setFieldValue(`actions[${index}].externalSourceUnitId`, null);
          }
        }}
        options={filteredOperators}
        label='label'
      />
    );

    if (enumOptions) {
      if (action.operator !== '=') {
        setFieldValue(`actions[${index}].operator`, '=');
      }

      return (
        <>
          {action.source !== 'WRCLocks' && operatorSelect}
          <CustomSelect
            disabled={disabled || !action.itemId || !action.action}
            className={classes.width118}
            error={getIn(errors, `actions[${index}].value`) && getIn(touched, `actions[${index}].value`)}
            placeholder={t`Value`}
            name='value'
            value={action.value}
            onChange={(event: any) => setFieldValue(`actions[${index}].value`, event.target.value)}
            options={enumOptions}
          />
        </>
      )
    }

    if (action.operator === 'external') {
      return (
        <>
          {operatorSelect}
          {action.source === 'systems' && (
            <CustomAutoComplete
              error={getIn(errors, `actions[${index}].externalSourceUnitId`) && getIn(touched, `actions[${index}].externalSourceUnitId`)}
              className={classes.width190}
              placeholder={t`Select Indoor Unit`}
              formLabel={t`Leading System Unit`}
              onChange={(event: any, value: any) => setFieldValue(`actions[${index}].externalSourceUnitId`, value)}
              value={action.externalSourceUnitId || null}
              options={externalUnitOptions}
              name="externalSourceUnitId"
            />
          )}
          <CustomSelect
            disabled={disabled || !action.itemId || !action.action}
            className={classes.minWidth160}
            error={getIn(errors, `actions[${index}].value`) && getIn(touched, `actions[${index}].value`)}
            placeholder={t`Value`}
            name='value'
            value={action.value}
            onChange={(event: any) => setFieldValue(`actions[${index}].value`, event.target.value)}
            options={externalSetpointOptions}
            label='label'
          />
        </>
      );
    }

    const isLabelLong = label.length > 10;
    const shortLabel = isLabelLong ? `${label.substring(0, 10)}...` : label;
    return (
      <>
        {action.source !== 'WRCLocks' && operatorSelect}
        <Tooltip title={label} placement='bottom' disableTooltip={!isLabelLong}>
          <span>
            <CustomedTextField
              disabled={disabled || !action.itemId || !action.action}
              className={classes.width118}
              error={getIn(errors, `actions[${index}].value`) && getIn(touched, `actions[${index}].value`)}
              onChange={(event: any) => setFieldValue(`actions[${index}].value`, event.target.value)}
              name='value'
              type={type}
              value={action.value}
              label={shortLabel}
            />
          </span>
        </Tooltip>
      </>
    );
  };

  return (
    <div className={clsx(classes.conditionContainer, classes.marginBottom)}>
      <CustomSelect
        className={classes.minWidth160}
        error={getIn(errors, `actions[${index}].source`) && getIn(touched, `actions[${index}].source`)}
        placeholder={t`Select Source`}
        formLabel={t`Source`}
        value={action.source}
        name="source"
        onChange={(event: any) => {
          setFieldValue(`actions[${index}]`, {
            ...action,
            source: event.target.value,
            itemId: '',
            action: '',
            value: '',
            operator: '',
            externalSourceUnitId: null,
            alertResourceType: event.target.value === 'alert' ? 'site' : '',
            description: event.target.value === 'alert' ? (action.description || '') : '',
          });
        }}
        options={expandedSourceOptions}
        disabled={disabled}
      />
      {action.source === "alert" &&
        <>
          <CustomSelect
            className={classes.minWidth160}
            placeholder={t`Attach Alert To`}
            formLabel={t`Attach Alert To`}
            name="alertResourceType"
            value={action.alertResourceType || 'site'}
            options={alertAttachToOptions}
            onChange={(event: any) => {
              setFieldValue(`actions[${index}]`, {
                ...action,
                alertResourceType: event.target.value,
                itemId: '',
              });
            }}
            disabled={disabled}
          />
          {!!action.alertResourceType && action.alertResourceType !== 'site' &&
            <CustomAutoComplete
              error={getIn(errors, `actions[${index}].itemId`) && getIn(touched, `actions[${index}].itemId`)}
              placeholder={selectItemPlaceholders[action.alertResourceType] || t`Select`}
              formLabel={selectItemLabels[action.alertResourceType] || t`Select`}
              onChange={(event: any, value: any) =>
                setFieldValue(`actions[${index}].itemId`, value)
              }
              value={action.itemId}
              options={site[action.alertResourceType] || []}
              name="itemId"
              disabled={disabled}
            />
          }
          <CustomedTextField
            className={classes.alertDescriptionField}
            placeholder={t`Description`}
            label={t`Description`}
            name="description"
            value={action.description || ''}
            onChange={(event: any) =>
              setFieldValue(`actions[${index}].description`, event.target.value)
            }
            disabled={disabled}
          />
        </>
      }
      {action.source !== "alert" &&
        <>
          <CustomAutoComplete
            error={getIn(errors, `actions[${index}].itemId`) && getIn(touched, `actions[${index}].itemId`)}
            placeholder={selectItemPlaceholders[action.source || ""]}
            formLabel={selectItemLabels[action.source || ""]}
            onChange={(event: any, value: any) => {
              if (action.source === 'bacNetUnits' || action.source === 'modbusUnits') {
                setFieldValue(`actions[${index}]`, {
                  ...action,
                  itemId: value,
                  value: '',
                  action: action.action === trapActionTypes.createEvent ? action.action : ''
                });
                return;
              }

              setFieldValue(`actions[${index}].itemId`, value);
              if (action.source === 'systems') {
                setFieldValue(`actions[${index}].externalSourceUnitId`, null);
              }
              if (action.source === "indoors" && action.itemId && (action.itemId.subType !== value?.subType)) {
                setFieldValue(`actions[${index}].value`, "");
                if (+action?.action === 3) {
                  setFieldValue(`actions[${index}].action`, "");
                }
              }
            }}
            value={action.itemId}
            options={action.source ? site[action.source] : []}
            name="itemId"
          />
          <CustomSelect
            disabled={disabled || !action.itemId}
            className={classes.actionSelect}
            error={getIn(errors, `actions[${index}].action`) && getIn(touched, `actions[${index}].action`)}
            placeholder={t`Select Parameter / Action`}
            formLabel={t`Parameter / Action`}
            name="action"
            value={action.action}
            options={actionOptions}
            onChange={(event: any) => {
              setFieldValue(`actions[${index}]`, {
                ...action,
                action: event.target.value,
                value: '',
                operator: '=',
                externalSourceUnitId: null,
              });
            }}
          />
          {renderValueField()}
        </>
      }
      <div className={classes.buttonsContainer}>
        {!!duplicateAction && <Tooltip title={t`Duplicate action`} >
          <Button
            disabled={disabled}
            onClick={duplicateAction}
            variant="text"
            size="small"
            className={classes.duplicateButton}
          >
            {t`Duplicate`}
          </Button>
        </Tooltip>
        }
        <IconButton
          disabled={disabled}
          className={clsx(classes.iconBtnStyle, classes.deleteIconNotInGroup)}
          onClick={deleteAction}
        >
          <Delete style={{ width: 20, height: 20 }} />
        </IconButton>
      </div>
    </div>
  );
};

export default Action;
